import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Loader2, ShoppingBag, Store, Building2, Briefcase, Sparkles } from "lucide-react";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";

const productCategories = [
  "Handmade & Artisan", "Corporate Gifting", "Home & Living", "Fashion & Accessories",
  "Beauty & Wellness", "Food & Beverage", "Sustainable Products", "Office & Workplace",
  "Manufacturing", "D2C Brands",
];
const serviceCategories = [
  "Consultants", "Coaches & Trainers", "HR Professionals", "Marketing & Branding",
  "Finance & Accounting", "Legal Services", "Technology & IT", "Creative Services",
  "Event Professionals", "Wellness Experts",
];

const businessRoles = [
  { key: "product_seller", icon: Store, title: "Product Seller", desc: "I sell physical products (handmade, fashion, food, home, etc.)", categories: productCategories },
  { key: "business_entity", icon: Building2, title: "Business Entity", desc: "I run a manufacturing / D2C brand / larger enterprise.", categories: productCategories },
  { key: "service_provider", icon: Briefcase, title: "Service Provider", desc: "I offer professional services (consulting, legal, marketing, tech, etc.)", categories: serviceCategories },
];

export default function Signup() {
  const nav = useNavigate();
  const location = useLocation();
  const preselect = new URLSearchParams(location.search).get("as");
  const [role, setRole] = useState(preselect === "buyer" ? "buyer" : preselect === "business" ? "product_seller" : null);
  const { signupBuyer, signupBusiness } = useAuth();

  const goDashboard = () => nav("/dashboard");

  return (
    <div className="min-h-screen bg-brand-paper" data-testid="signup-page">
      <div className="max-w-6xl mx-auto px-6 md:px-10 py-10">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-brand-muted hover:text-brand-wine transition-colors" data-testid="signup-back-link">
          <ArrowLeft size={16} /> Back to Yuukke
        </Link>
        <div className="mt-6 flex items-center justify-between flex-wrap gap-4">
          <img src="/yuukke-logo.png" alt="Yuukke" className="h-9 w-auto" />
          <p className="text-sm text-brand-muted">
            Already a member?{" "}
            <Link to="/login" className="text-brand-wine font-semibold hover:underline" data-testid="signup-login-link">
              Sign in
            </Link>
          </p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 md:px-10 pb-24">
        {!role ? (
          <RoleSelector onPick={setRole} />
        ) : role === "buyer" ? (
          <BuyerForm onBack={() => setRole(null)} onSubmit={async (p) => { await signupBuyer(p); toast.success("Welcome to Yuukke"); goDashboard(); }} />
        ) : (
          <BusinessOwnerForm
            businessType={role}
            onBack={() => setRole(null)}
            onSwitch={(k) => setRole(k)}
            onSubmit={async (p) => { await signupBusiness(p); toast.success("Application submitted. AI profile is generating..."); goDashboard(); }}
          />
        )}
      </div>
    </div>
  );
}

/* ---------- Role picker ---------- */
function RoleSelector({ onPick }) {
  return (
    <div>
      <div className="mt-10 text-center max-w-2xl mx-auto">
        <span className="eyebrow" data-testid="signup-eyebrow">Join the Business Exchange</span>
        <h1 className="mt-4 font-serif text-5xl md:text-6xl font-medium tracking-[-0.01em] leading-[1.05] text-brand-ink">
          Choose your <span className="text-gradient-signature italic">path</span>
        </h1>
        <p className="mt-5 text-brand-muted text-lg">
          Yuukke welcomes both organizations looking to source and women-led businesses looking to grow.
        </p>
      </div>

      <div className="mt-12 grid md:grid-cols-2 gap-6">
        <motion.button
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          onClick={() => onPick("buyer")}
          className="group text-left bg-brand-cream border border-brand-line rounded-2xl p-8 md:p-10 hover:-translate-y-1 hover:shadow-lift transition-all duration-500"
          data-testid="role-buyer-card"
        >
          <div className="w-14 h-14 rounded-full bg-brand-blush flex items-center justify-center mb-6">
            <ShoppingBag size={22} className="text-brand-wine" />
          </div>
          <span className="text-xs uppercase tracking-[0.2em] font-semibold text-brand-wine">Buyer / Sourcing Team</span>
          <h3 className="mt-2 font-serif text-3xl font-medium text-brand-ink">I&apos;m looking to <span className="italic">source</span>.</h3>
          <p className="mt-3 text-brand-muted text-[15px] leading-relaxed">
            Discover curated women-led businesses, save shortlists, post sourcing RFPs, and connect directly with vetted suppliers.
          </p>
          <span className="mt-6 inline-flex items-center gap-2 text-brand-wine font-semibold group-hover:gap-3 transition-all">
            Continue as buyer <ArrowRight size={16} />
          </span>
        </motion.button>

        <motion.button
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          onClick={() => onPick("product_seller")}
          className="group text-left bg-brand-wine text-brand-cream border border-brand-wine rounded-2xl p-8 md:p-10 hover:-translate-y-1 hover:shadow-lift transition-all duration-500 relative overflow-hidden"
          data-testid="role-business-card"
        >
          <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-white/10" />
          <div className="w-14 h-14 rounded-full bg-white/10 border border-white/20 flex items-center justify-center mb-6 relative">
            <Sparkles size={22} />
          </div>
          <span className="text-xs uppercase tracking-[0.2em] font-semibold text-brand-cream/80 relative">Business Owner</span>
          <h3 className="mt-2 font-serif text-3xl font-medium relative">I run a <span className="italic">women-led business</span>.</h3>
          <p className="mt-3 text-brand-cream/85 text-[15px] leading-relaxed relative">
            Get discovered, receive an instant AI procurement-readiness score, and connect with buyers. Products, brands, or professional services — all welcome.
          </p>
          <span className="mt-6 inline-flex items-center gap-2 text-brand-cream font-semibold group-hover:gap-3 transition-all relative">
            Continue as business <ArrowRight size={16} />
          </span>
        </motion.button>
      </div>
    </div>
  );
}

/* ---------- Buyer form ---------- */
function BuyerForm({ onBack, onSubmit }) {
  const [form, setForm] = useState({ full_name: "", email: "", password: "", organization: "", role_title: "" });
  const [busy, setBusy] = useState(false);
  const u = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.full_name.length < 2) return toast.error("Please enter your full name.");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return toast.error("Enter a valid email.");
    if (form.password.length < 8) return toast.error("Password must be at least 8 characters.");
    setBusy(true);
    try { await onSubmit(form); }
    catch (err) { toast.error(err?.response?.data?.detail || "Signup failed"); }
    finally { setBusy(false); }
  };

  return (
    <FormShell
      onBack={onBack}
      eyebrow="Buyer / Sourcing Team"
      title="Create your buyer account"
      subtitle="Save shortlists, post sourcing RFPs, and connect with curated women-led suppliers."
      testid="buyer-form"
    >
      <form onSubmit={submit} noValidate className="space-y-4" data-testid="buyer-signup-form">
        <Grid2>
          <Field label="Full name *"><Input value={form.full_name} onChange={(e) => u("full_name", e.target.value)} placeholder="Ravi Kumar" testid="buyer-name" /></Field>
          <Field label="Work email *"><Input type="email" value={form.email} onChange={(e) => u("email", e.target.value)} placeholder="ravi@acme.com" testid="buyer-email" /></Field>
        </Grid2>
        <Field label="Password *"><Input type="password" value={form.password} onChange={(e) => u("password", e.target.value)} placeholder="Min 8 characters" testid="buyer-password" /></Field>
        <Grid2>
          <Field label="Organization"><Input value={form.organization} onChange={(e) => u("organization", e.target.value)} placeholder="Acme Corp" testid="buyer-org" /></Field>
          <Field label="Your role"><Input value={form.role_title} onChange={(e) => u("role_title", e.target.value)} placeholder="Procurement Lead" testid="buyer-title" /></Field>
        </Grid2>
        <SubmitBtn busy={busy} label="Create buyer account" testid="buyer-signup-submit" />
      </form>
    </FormShell>
  );
}

/* ---------- Business owner form ---------- */
function BusinessOwnerForm({ businessType, onBack, onSwitch, onSubmit }) {
  const [form, setForm] = useState({
    full_name: "", email: "", password: "", business_name: "", category: "",
    city: "", phone: "", website: "", description: "",
  });
  const [busy, setBusy] = useState(false);
  const active = businessRoles.find((r) => r.key === businessType) || businessRoles[0];

  const u = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.full_name.length < 2) return toast.error("Please enter your full name.");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return toast.error("Enter a valid email.");
    if (form.password.length < 8) return toast.error("Password must be at least 8 characters.");
    if (form.business_name.length < 1) return toast.error("Enter your business name.");
    if (!form.category) return toast.error("Pick a category.");
    if (form.description.length < 10) return toast.error("Describe your business (min 10 characters).");
    setBusy(true);
    try { await onSubmit({ ...form, business_type: businessType }); }
    catch (err) { toast.error(err?.response?.data?.detail || "Signup failed"); }
    finally { setBusy(false); }
  };

  return (
    <FormShell
      onBack={onBack}
      eyebrow="Business Owner"
      title="Create your business account"
      subtitle="You&apos;ll receive an instant AI-generated readiness score and a curated profile after signup."
      testid="business-form"
    >
      <div className="mb-6 flex flex-wrap gap-2" data-testid="business-type-picker">
        {businessRoles.map((r) => {
          const Icon = r.icon;
          const active = r.key === businessType;
          return (
            <button
              key={r.key}
              type="button"
              onClick={() => onSwitch(r.key)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium transition-all ${
                active
                  ? "bg-brand-wine text-white border-brand-wine"
                  : "bg-brand-cream text-brand-ink border-brand-line hover:border-brand-wine/40"
              }`}
              data-testid={`business-type-${r.key}`}
            >
              <Icon size={14} /> {r.title}
            </button>
          );
        })}
      </div>

      <p className="mb-5 text-sm text-brand-muted italic">{active.desc}</p>

      <form onSubmit={submit} noValidate className="space-y-4" data-testid="business-signup-form">
        <Grid2>
          <Field label="Full name *"><Input value={form.full_name} onChange={(e) => u("full_name", e.target.value)} placeholder="Maya Roy" testid="biz-name" /></Field>
          <Field label="Email *"><Input type="email" value={form.email} onChange={(e) => u("email", e.target.value)} placeholder="you@business.com" testid="biz-email" /></Field>
        </Grid2>
        <Field label="Password *"><Input type="password" value={form.password} onChange={(e) => u("password", e.target.value)} placeholder="Min 8 characters" testid="biz-password" /></Field>
        <Grid2>
          <Field label="Business name *"><Input value={form.business_name} onChange={(e) => u("business_name", e.target.value)} placeholder="Maya Crafts" testid="biz-business-name" /></Field>
          <Field label="Category *">
            <Select value={form.category} onValueChange={(v) => u("category", v)}>
              <SelectTrigger className="bg-brand-cream border-brand-line rounded-lg h-[50px]" data-testid="biz-category">
                <SelectValue placeholder="Select a category" />
              </SelectTrigger>
              <SelectContent className="bg-brand-cream border-brand-line max-h-72">
                {active.categories.map((c) => (
                  <SelectItem key={c} value={c} data-testid={`biz-category-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
        </Grid2>
        <Grid2>
          <Field label="City"><Input value={form.city} onChange={(e) => u("city", e.target.value)} placeholder="Chennai" testid="biz-city" /></Field>
          <Field label="Phone"><Input value={form.phone} onChange={(e) => u("phone", e.target.value)} placeholder="+91 98765 43210" testid="biz-phone" /></Field>
        </Grid2>
        <Field label="Website"><Input value={form.website} onChange={(e) => u("website", e.target.value)} placeholder="https://…" testid="biz-website" /></Field>
        <Field label="Tell us about your business *">
          <textarea
            value={form.description}
            onChange={(e) => u("description", e.target.value)}
            rows={4}
            placeholder="What do you offer, who do you serve, and what makes you unique?"
            className="w-full bg-brand-cream border border-brand-line rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-brand-wine/20 focus:border-brand-wine transition-all text-brand-ink resize-none"
            data-testid="biz-description"
          />
        </Field>
        <SubmitBtn busy={busy} label="Create business account & score me" icon={<Sparkles size={16} />} testid="business-signup-submit" />
      </form>
    </FormShell>
  );
}

/* ---------- Reusable UI ---------- */
function FormShell({ onBack, eyebrow, title, subtitle, testid, children }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      className="mt-10 bg-brand-cream border border-brand-line rounded-2xl p-6 md:p-10 shadow-lift-sm"
      data-testid={testid}
    >
      <button
        onClick={onBack}
        className="inline-flex items-center gap-2 text-sm text-brand-muted hover:text-brand-wine transition-colors mb-6"
        data-testid={`${testid}-back`}
      >
        <ArrowLeft size={14} /> Change path
      </button>
      <span className="eyebrow">{eyebrow}</span>
      <h2 className="mt-4 font-serif text-4xl md:text-5xl font-medium tracking-[-0.01em] leading-[1.05] text-brand-ink">{title}</h2>
      <p className="mt-3 text-brand-muted text-[15px] max-w-lg leading-relaxed">{subtitle}</p>
      <div className="mt-8">{children}</div>
    </motion.div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[13px] font-medium text-brand-ink mb-1.5">{label}</span>
      {children}
    </label>
  );
}
function Input({ testid, ...props }) {
  return (
    <input
      {...props}
      data-testid={testid}
      className="w-full bg-brand-cream border border-brand-line rounded-lg px-4 py-3 h-[50px] focus:outline-none focus:ring-2 focus:ring-brand-wine/20 focus:border-brand-wine transition-all text-brand-ink placeholder:text-brand-muted/50"
    />
  );
}
function Grid2({ children }) {
  return <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{children}</div>;
}
function SubmitBtn({ busy, label, icon, testid }) {
  return (
    <button
      type="submit"
      disabled={busy}
      className="w-full mt-2 bg-brand-wine text-white rounded-lg py-4 font-semibold hover:bg-brand-wineDark hover:-translate-y-0.5 transition-all flex items-center justify-center gap-2 disabled:opacity-70"
      data-testid={testid}
    >
      {busy ? <><Loader2 size={16} className="animate-spin" /> Creating account…</> : <>{icon}{label}</>}
    </button>
  );
}
